import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Trash2, Loader2, FileText, File } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ContractDocument, Contract } from "@/types";
import { useState, useRef } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { uploadDocuments, deleteDocument, downloadDocument } from "@/api/documents";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface DocumentsCardProps {
  contract: Contract;
  documents: ContractDocument[];
  onDocumentsChange: () => void;
  disabled?: boolean;
}

export function DocumentsCard({
  contract,
  documents,
  onDocumentsChange,
  disabled = false
}: DocumentsCardProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayDocuments = documents.length > 0 ? documents : (contract.documents || []);

  const isDisabled = disabled || contract.status === 'active' || contract.status === 'complete';

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Validate file sizes (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const invalidFiles = fileArray.filter(file => file.size > maxSize);
    
    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed the 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Validate file types
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
    const invalidTypes = fileArray.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidTypes.length > 0) {
      toast.error(`Invalid file types. Only PDF, JPG, PNG, GIF, WebP, and HEIC are allowed.`);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadDocuments(
        contract._id,
        fileArray,
        'identification', // document type
        (progressEvent: any) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      );

      if (response && response.success) {
        toast.success(`${fileArray.length} document(s) uploaded successfully`);
        onDocumentsChange();
      } else {
        toast.error(response?.message || "Failed to upload documents");
      }
    } catch (error) {
      toast.error("Error uploading documents");
      console.error(error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocumentClick = (documentId: number) => {
    if (isDisabled) return;
    setDocumentToDelete(documentId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete || isDisabled) return;

    try {
      setIsDeletingDocument(true);
      const response = await deleteDocument(contract._id, documentToDelete);
      
      if (response && response.success) {
        toast.success("Document deleted successfully");
        onDocumentsChange();
      } else {
        toast.error(response?.message || "Failed to delete document");
      }
    } catch (error) {
      toast.error("Error deleting document");
      console.error(error);
    } finally {
      setIsDeletingDocument(false);
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDownloadDocument = async (documentId: number, originalName: string) => {
    try {
      const response = await downloadDocument(contract._id, documentId);
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Document downloaded successfully");
    } catch (error) {
      toast.error("Error downloading document");
      console.error(error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) {
      return <FileText className="h-4 w-4 text-red-500" />;
    }
    return <File className="h-4 w-4 text-blue-500" />;
  };

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center">
          <CardTitle>Upload Documents</CardTitle>
        </div>
        <div className="flex gap-2 items-center">
          {isUploading && (
            <div className="flex items-center gap-2 min-w-[150px]">
              <Progress value={uploadProgress} className="w-full" />
              <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isDisabled || isUploading}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
            disabled={isDisabled || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload Documents
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[200px] rounded-md">
          <div className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Uploaded At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No documents uploaded
                    </TableCell>
                  </TableRow>
                ) : (
                  displayDocuments.map((doc) => (
                    <TableRow key={doc._id}>
                      <TableCell className="flex items-center gap-2">
                        {getFileIcon(doc.mimeType)}
                        <span className="font-medium">{doc.originalName}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {doc.documentType || 'KYC'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      <TableCell>
                        {doc.uploadedBy ? (
                          <div className="flex flex-col">
                            <span className="text-sm">{doc.uploadedBy.name}</span>
                            <span className="text-xs text-muted-foreground">{doc.uploadedBy.role}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Unknown</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(doc.uploadedAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDocument(doc._id, doc.originalName)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <AlertDialog open={isDeleteDialogOpen && documentToDelete === doc._id} onOpenChange={setIsDeleteDialogOpen}>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDocumentClick(doc._id)}
                                disabled={isDisabled}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Document</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{doc.originalName}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={confirmDeleteDocument}
                                  disabled={isDeletingDocument}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  {isDeletingDocument ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Deleting...
                                    </>
                                  ) : (
                                    'Delete'
                                  )}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
